'use client';

import { FC, useEffect } from 'react';
import { Content } from '@prismicio/client';
import { JSXMapSerializer, PrismicRichText } from '@prismicio/react';
import { TitleComponentsParams } from '@/types';

import styles from './instructionList.module.scss';
import { Simplify } from '../../../prismicio-types';

const titleComponents = ({
  variant,
  index,
}: TitleComponentsParams): JSXMapSerializer => ({
  paragraph: ({ children }) => {
    const isNumbered =
      variant === 'numbered' || variant === 'numberedSmallWithTitle';

    return (
      <p className={`${styles.title} ${styles[variant]}`}>
        {isNumbered && <span>{index + 1}. </span>}
        {children}
      </p>
    );
  },
});

const descriptionComponents = (
  list_variant: 'numbered' | 'nested'
): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p className={styles.descriptionText}>{children}</p>
  ),
  listItem: ({ children }) => (
    <li className={`${styles.descriptionText} ${styles.listItem}`}>
      <span className={styles.square}>&#9632;</span>
      <span>{children}</span>
    </li>
  ),
  oList: ({ children }) => <ol className={styles[list_variant]}>{children}</ol>,
});

type Props = {
  list: Simplify<Content.TermsInstrictionSliceDefaultPrimaryInstructionItem>[];
  variation:
    | 'default'
    | 'numbered'
    | 'numberedSmallWithTitle'
    | 'unnumberedSmallWithTitle'
    | 'unnumberedWithSubtitle'
    | 'defaultWithoutTitle';
};

export const InstructionList: FC<Props> = ({ list, variation }) => {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, []);

  return (
    <ul className={`${styles.instruction} ${styles[variation]}`}>
      {list.map((instruction, index) => {
        const hasTitle = 'title' in instruction && instruction.title;

        return (
          <li
            className={`${styles.instructionItem} ${styles[variation]}`}
            key={hasTitle ? `${instruction.title[0]?.type}-${index}` : index}
            id={instruction.section_id ?? ''}
          >
            {hasTitle && (
              <PrismicRichText
                field={instruction.title}
                components={titleComponents({
                  variant: variation,
                  index,
                })}
              />
            )}
            <div
              className={`${styles.descriptionWrapper} ${styles[variation]}`}
            >
              <PrismicRichText
                field={instruction.description}
                components={descriptionComponents(instruction.list_variant)}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
};
